/**
 * <ImageHotspots /> — يعرض نقاطاً تفاعليّة فوق صورة/فيديو.
 *
 * يجب أن يكون العنصر الأب `position: relative` ويحتوي على <img> أو <video poster=>
 * كمصدر للأبعاد الطبيعيّة. النقاط تُحفَظ كنسبة من الصورة الطبيعيّة، ويتم تحويلها
 * إلى موقع الحاوية الفعليّ مع احتساب object-cover والـobject-position المحسوب.
 */
import * as React from "react";
import { ArrowUpRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useImageHotspots } from "@/hooks/useImageHotspots";
import { useLocale } from "@/contexts/LocaleContext";
import { mapImagePctToContainer } from "@/lib/imageCoverMap";
import type { PublicImageHotspot } from "@/lib/publicImageHotspots";

interface Props {
  scope: string;
  scopeId: string | undefined | null;
  /** يظهر النقاط حتى مع عدم وجود محتوى (للمعاينة في المحرّر). */
  alwaysVisible?: boolean;
  /** نمط fit للصورة الأبويّة. افتراضي cover. */
  fit?: "cover" | "contain";
}

interface Dims {
  cw: number;
  ch: number;
  iw: number;
  ih: number;
  posX: number;
  posY: number;
}

function parsePos(value: string): number {
  // يدعم "50%" أو "12.3%" أو "0px" (نفترض 50 لو px)
  const m = /(-?\d+(\.\d+)?)%/.exec(value);
  if (m) return parseFloat(m[1]);
  return 50;
}

export function ImageHotspots({ scope, scopeId, alwaysVisible, fit = "cover" }: Props) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const { data } = useImageHotspots(scope, scopeId);
  const items = data ?? [];

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dims, setDims] = React.useState<Dims | null>(null);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const parent = el.parentElement;
    if (!parent) return;

    const measure = () => {
      const img = parent.querySelector("img") as HTMLImageElement | null;
      const video = parent.querySelector("video") as HTMLVideoElement | null;
      const rect = parent.getBoundingClientRect();
      let iw = 0;
      let ih = 0;
      let posX = 50;
      let posY = 50;
      if (img && img.naturalWidth) {
        iw = img.naturalWidth;
        ih = img.naturalHeight;
        const cs = getComputedStyle(img);
        const [px, py] = (cs.objectPosition || "50% 50%").split(/\s+/);
        posX = parsePos(px || "50%");
        posY = parsePos(py || "50%");
      } else if (video) {
        iw = video.videoWidth || 1920;
        ih = video.videoHeight || 1080;
        const cs = getComputedStyle(video);
        const [px, py] = (cs.objectPosition || "50% 50%").split(/\s+/);
        posX = parsePos(px || "50%");
        posY = parsePos(py || "50%");
      } else {
        // fallback: نسبة الحاوية كأنها هي الصورة
        iw = rect.width || 1;
        ih = rect.height || 1;
      }
      setDims({ cw: rect.width, ch: rect.height, iw, ih, posX, posY });
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(parent);

    const img = parent.querySelector("img") as HTMLImageElement | null;
    if (img && !img.complete) img.addEventListener("load", measure);
    const video = parent.querySelector("video") as HTMLVideoElement | null;
    if (video) video.addEventListener("loadedmetadata", measure);

    return () => {
      ro.disconnect();
      if (img) img.removeEventListener("load", measure);
      if (video) video.removeEventListener("loadedmetadata", measure);
    };
  }, [items.length]);

  if (!items.length && !alwaysVisible) return null;

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20"
    >
      {items.map((h: PublicImageHotspot, idx: number) => {
        const title = (isAr ? h.title_ar : h.title_en) || h.title_en || h.title_ar || "";
        const body = (isAr ? h.body_ar : h.body_en) || h.body_en || h.body_ar || "";
        const hasContent = title || body || h.image_url || h.link_url;

        const mapped = dims
          ? mapImagePctToContainer({
              xImgPct: h.x_pct,
              yImgPct: h.y_pct,
              containerW: dims.cw,
              containerH: dims.ch,
              imgW: dims.iw,
              imgH: dims.ih,
              posX: dims.posX,
              posY: dims.posY,
              fit,
            })
          : { leftPct: h.x_pct, topPct: h.y_pct, visible: true };

        if (!mapped.visible) return null;

        return (
          <Popover key={h.id}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={title || (isAr ? `نقطة ${idx + 1}` : `Point ${idx + 1}`)}
                className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${mapped.leftPct}%`, top: `${mapped.topPct}%` }}
              >
                <span className="relative flex h-6 w-6 items-center justify-center">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50 opacity-75" />
                  <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold  ring-2 ring-white/90 transition-transform group-hover:scale-110">
                    {idx + 1}
                  </span>
                </span>
              </button>
            </PopoverTrigger>
            {hasContent && (
              <PopoverContent
                className="w-72 p-0 overflow-hidden"
                side="top"
                align="center"
                sideOffset={10}
              >
                {h.image_url && (
                  <img
                    src={h.image_url}
                    alt={title}
                    className="aspect-[16/10] w-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="p-4">
                  {title && <h4 className="t-h4 text-foreground">{title}</h4>}
                  {body && (
                    <p className="mt-1.5 t-body whitespace-pre-line text-foreground/80 leading-relaxed">
                      {body}
                    </p>
                  )}
                  {h.link_url && (
                    <a
                      href={h.link_url}
                      target={h.link_url.startsWith("http") ? "_blank" : undefined}
                      rel={h.link_url.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="mt-3 inline-flex items-center gap-1 t-eyebrow text-primary"
                    >
                      {isAr ? "اعرف المزيد" : "Learn more"}
                      <ArrowUpRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                    </a>
                  )}
                </div>
              </PopoverContent>
            )}
          </Popover>
        );
      })}
    </div>
  );
}
